import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Slider, CircularProgress, Divider } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import ReplayIcon from '@mui/icons-material/Replay';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import apiClient from '../services/apiClient';
import transcriptionService, { AudioRecordingItem } from '../services/transcriptionService';
import { typography } from '~/theme';

interface AudioPanelProps {
  sessionId: string | null;
  isTranscribing: boolean;
  isFinal: boolean;
  hasAudio?: boolean;
}

// ── Single recording player ──────────────────────────────────────────────────

interface RecordingPlayerProps {
  index: number;
  total: number;
  recording: AudioRecordingItem;
  sessionId: string;
}

const RecordingPlayer: React.FC<RecordingPlayerProps> = ({ index, total, recording, sessionId }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = transcriptionService.getRecordingAudioUrl(sessionId, recording.id);
    setLoading(true);
    setError(null);
    apiClient.get(url, { responseType: 'blob' })
      .then((resp) => {
        const blob = new Blob([resp.data], { type: 'audio/wav' });
        setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));

    return () => {
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [sessionId, recording.id]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !blobUrl) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, [blobUrl]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); } else { audio.play(); }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleStop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleReplay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play();
    setIsPlaying(true);
  }, []);

  const handleSeek = useCallback((_: Event, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value as number;
    setCurrentTime(value as number);
  }, []);

  return (
    <Box>
      {total > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: typography.label.fontWeight }}>
          Recording {index + 1} of {total}
          {recording.duration_seconds > 0 && ` · ${formatTime(recording.duration_seconds)}`}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Loading...</Typography>
        </Box>
      )}

      {error && !loading && (
        <Typography variant="caption" color="error">{error}</Typography>
      )}

      {blobUrl && !loading && (
        <Box>
          <audio ref={audioRef} src={blobUrl} preload="metadata" />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <IconButton onClick={handlePlayPause} color="primary" size="small">
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <IconButton onClick={handleStop} size="small">
              <StopIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleReplay} size="small" title="Replay from start">
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" sx={{ minWidth: 32, textAlign: 'right', color: 'text.secondary' }}>
              {formatTime(currentTime)}
            </Typography>
            <Slider
              value={currentTime}
              max={duration || 1}
              onChange={handleSeek}
              size="small"
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ minWidth: 32, color: 'text.secondary' }}>
              {formatTime(duration)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── AudioPanel ───────────────────────────────────────────────────────────────

const AudioPanel: React.FC<AudioPanelProps> = ({ sessionId, isTranscribing, isFinal, hasAudio = false }) => {
  const [recordings, setRecordings] = useState<AudioRecordingItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const prevTranscribingRef = useRef(false);
  // refreshKey increments each time a recording session ends so the effect re-fires
  // even when sessionId/isFinal/hasAudio haven't changed (continue recording case)
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const justStopped = prevTranscribingRef.current && !isTranscribing;
    prevTranscribingRef.current = isTranscribing;
    if (justStopped) {
      setRefreshKey((k) => k + 1);
    }
  }, [isTranscribing]);

  useEffect(() => {
    if (!sessionId || isTranscribing || !isFinal || !hasAudio) {
      setRecordings([]);
      return;
    }
    // Small delay after a live recording ends so the backend has time to
    // finalize and commit the new AudioRecording row before we query it.
    const delay = refreshKey > 0 ? 1500 : 0;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setListLoading(true);
      transcriptionService.getSessionRecordings(sessionId)
        .then((recs) => { if (!cancelled) setRecordings(recs); })
        .catch(() => { if (!cancelled) setRecordings([]); })
        .finally(() => { if (!cancelled) setListLoading(false); });
    }, delay);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId, isTranscribing, isFinal, hasAudio, refreshKey]);

  if (isTranscribing) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <HeadphonesIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="caption" color="text.disabled" display="block">Dictation in progress...</Typography>
      </Box>
    );
  }

  if (listLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CircularProgress size={24} sx={{ mb: 0.5 }} />
        <Typography variant="caption" color="text.secondary" display="block">Loading audio...</Typography>
      </Box>
    );
  }

  if (!sessionId || recordings.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <HeadphonesIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="caption" color="text.disabled" display="block">No dictation recording available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {recordings.map((rec, idx) => (
        <Box key={rec.id}>
          <RecordingPlayer
            index={idx}
            total={recordings.length}
            recording={rec}
            sessionId={sessionId}
          />
          {idx < recordings.length - 1 && <Divider sx={{ mt: 1.5 }} />}
        </Box>
      ))}
    </Box>
  );
};

export default AudioPanel;
